import { logout } from "@src/modules/session";
import { loadLazily } from "@src/utils/loadLazily";
import { routes } from "@src/appV2/redesign/utils/routes";
import { Modal } from "antd";
import firebase from "firebase/app";
import { useCallback, useEffect, useState } from "react";
import { Redirect, Route, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { SessionType } from "@src/modules/interface";
import { useInitializeLaunchDarkly } from "@src/hooks/useInitializeLaunchDarkly";
import { initBraze } from "@src/utils/braze";
import qs from "query-string";

import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";
import { AdminMenuPriviligesProvider } from "./Shared/Navigation/priviligesProviders/AdminMenuPriviligesProvider";
import { WorkplaceMenuPriviligesProvider } from "./Shared/Navigation/priviligesProviders/WorkplaceMenuPriviligesProvider";
import { signInWithCustomToken } from "@src/containers/enter/firebase";
import { ConnectedOmnisearch } from "@src/containers/app/omnisearch";
import { AppLayoutDesktop } from "./Desktop/AppLayoutDesktop";
import { useIsMobile } from "@src/appV2/redesign/AppLayout/useResponsive";
import { AppLayoutMobile } from "./Mobile/AppLayoutMobile";
import { WorkerMenuPriviligesProvider } from "./Shared/Navigation/priviligesProviders/WorkerMenuPriviligesProvider";
import { FloatingTabsProvider } from "@src/appV2/redesign/AppLayout/Mobile/FloatingTabsContext";

interface NavigationMenuContainerProps {
  isAdmin: boolean;
  isAgent: boolean;
  isFacility: boolean;
  session: any;
  location: any;
  get: () => void;
  getAdminProfile: () => void;
  loadingAdminProfile: boolean;
  initFirebaseSession: () => Promise<void>;
  getLocations: () => void;
  getEmployees: () => void;
}

const AdminAppLogin = loadLazily(
  async () =>
    await import("@src/containers/enter/commonLogin").then((module) => ({
      default: module.AdminAppLogin,
    }))
);

const FirebaseLoginVerify = loadLazily(
  async () =>
    await import("@src/containers/enter/commonLogin").then((module) => ({
      default: module.FirebaseLoginVerify,
    }))
);

function logoutConfirm() {
  Modal.confirm({
    title: "Are you sure you want to logout?",
    onOk: logout,
  });
}

export function NavigationMenuContainer(props: NavigationMenuContainerProps): JSX.Element {
  const {
    isAdmin,
    isAgent: isWorker,
    isFacility: isWorkplace,
    session,
    location,
    get,
    getAdminProfile,
    loadingAdminProfile,
    initFirebaseSession,
    getLocations,
    getEmployees,
  } = props;

  const [loading, setLoading] = useState(true);
  const { ready, profile } = useSelector((state: SessionType) => state.session || {});
  const isAuthorized = isAdmin || isWorker || isWorkplace;

  const history = useHistory();

  // get responsive mode
  const isMobile = useIsMobile();

  // init launch darkly
  useInitializeLaunchDarkly({ profile, isAdmin });

  // Initialize admin profile
  useEffect(() => {
    if (loadingAdminProfile) {
      getAdminProfile();
    }
  }, [loadingAdminProfile, getAdminProfile]);

  // Initialize Firebase session
  useEffect(() => {
    async function load() {
      setLoading(true);
      await initFirebaseSession();
      setLoading(false);
    }
    load();
  }, [initFirebaseSession]);

  // Initialize Braze (non-blocking, runs in background)
  useEffect(() => {
    if (session?.user?._id && !session.admin) {
      async function load() {
        await initBraze(session?.user?._id);
      }
      load();
    }
  }, [session?.admin, session?.user?._id]);

  // Fetch session data and locations
  useEffect(() => {
    if (loading || !ready) {
      return;
    }
    if (!isAuthorized) {
      return;
    }
    get();
    getLocations();

    if (session?.admin) {
      getEmployees();
    }
  }, [isAuthorized, get, getLocations, getEmployees, loading, ready, session?.admin]);

  // init firebase token
  const qString = qs.parse(location.search);
  const [isToken, setToken] = useState<any>(qString.token);
  const checkForAccessToken = useCallback(async (token) => {
    const result = await signInWithCustomToken(token);
    if ("error" in result) {
      setToken(false);
    } else {
      setToken(true);
    }
  }, []);

  useEffect(() => {
    if (loading || !ready) {
      return;
    }
    if (isToken) {
      checkForAccessToken(isToken);
    }
  }, [checkForAccessToken, loading, ready]);

  // redirect to the login page, if the user is not authenticated and not use a verification link
  useEffect(() => {
    const currentPath = window.location.pathname;

    return firebase.auth().onAuthStateChanged((user) => {
      // Skip redirect during the email verification process
      const isVerificationPage = currentPath.includes("login/verify");

      if (!user && !isVerificationPage) {
        history.replace("/login");
      }
    });
  }, [history]);

  function renderStartPage() {
    if (isAdmin) {
      return <Redirect to={routes.adminAccountsAgents} />;
    }

    if (isWorker) {
      return <Redirect to={routes.agentDashboard} />;
    }

    if (isWorkplace) {
      return <Redirect to={routes.agentDashboard} />;
    }

    return null;
  }

  // Early return for login routes - these should always render
  const isLoginRoute =
    location.pathname === routes.login || location.pathname === routes.loginVerify;

  if (isLoginRoute) {
    return (
      <BerlinThemeProvider>
        <Route exact path={routes.login} component={AdminAppLogin} />
        <Route exact path={routes.loginVerify} component={FirebaseLoginVerify} />
      </BerlinThemeProvider>
    );
  }

  // Guard to prevent rendering while session is initializing
  // This matches the old AppLayout logic (line 918 in appLayout.tsx)
  // Prevents API calls from firing before auth token and profile data are ready
  //
  // Important: ready becomes true BEFORE fetchSession() completes loading the full profile
  // We need to wait for profile.tmz to exist as it's required by API calls in AgentDashboard
  const hasRequiredProfileData = profile && profile.userId;

  if ((loading || !ready) && session.userId) {
    return <></>;
  }

  // Additional guard: ensure profile data is fully loaded before rendering
  // AgentDashboard needs profile.tmz, profile.geoLocation, profile.preference, etc.
  if (!hasRequiredProfileData) {
    return <></>;
  }

  return (
    <BerlinThemeProvider>
      <AdminMenuPriviligesProvider>
        <WorkplaceMenuPriviligesProvider>
          <WorkerMenuPriviligesProvider>
            <FloatingTabsProvider>
              <ConnectedOmnisearch />
              {isMobile ? (
                <AppLayoutMobile
                  isAdmin={isAdmin}
                  isWorker={isWorker}
                  isWorkplace={isWorkplace}
                  logoutConfirm={logoutConfirm}
                  renderStartPage={renderStartPage}
                />
              ) : (
                <AppLayoutDesktop
                  isAdmin={isAdmin}
                  isWorker={isWorker}
                  isWorkplace={isWorkplace}
                  logoutConfirm={logoutConfirm}
                  renderStartPage={renderStartPage}
                />
              )}
            </FloatingTabsProvider>
          </WorkerMenuPriviligesProvider>
        </WorkplaceMenuPriviligesProvider>
      </AdminMenuPriviligesProvider>
    </BerlinThemeProvider>
  );
}
